import React, { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';

import Loader from '~/components/General/Loader';

import './styles.scss';

const DEFAULT_MIN_WIDTH = 40;
const DEFAULT_MAX_WIDTH = 480;
const CONTROL_COLUMN_WIDTH = 72;
const DEFAULT_COLUMN_HEADER_HEIGHT = 48;
const DEFAULT_ROW_HEIGHT = 52;
const DEFAULT_PAGE_SIZE = 20;
const TABLE_LAYOUT_CONTAINER_SELECTOR =
  '.olcTableGridInternal, .olcPrimaryListTable, .olcAdminListTable';
const SIZE_CHANGE_TOLERANCE = 1;
// Match the 24px table-edge inset used by the settings tables. Keeping this
// value shared with the CSS edge token prevents compact columns from carrying
// an extra legacy 16px on every field (which was enough to create false
// overflow on Templates and Campaigns).
const CELL_GUTTER = 24;
const INTER_COLUMN_SPACING = 20;
const RIGHT_ACTION_CELL_CLASS = 'olcDataGridRightActionCell';
const RIGHT_ACTION_HEADER_CLASS = 'olcDataGridRightActionHeader';
const RESPONSIVE_TEXT_CLASS = 'olcDataGridResponsiveText';

const getLayoutContainer = (gridRoot) =>
  gridRoot?.closest(TABLE_LAYOUT_CONTAINER_SELECTOR) ?? gridRoot?.parentElement;

const getAvailableGridHeight = (gridRoot, layoutContainer) => {
  if (!gridRoot || !layoutContainer) return 0;

  const visibleChildren = Array.from(layoutContainer.children).filter((child) => {
    const styles = window.getComputedStyle(child);
    return styles.display !== 'none' && styles.position !== 'absolute';
  });
  const gridHost = visibleChildren.find((child) => child === gridRoot || child.contains(gridRoot));

  // Fall back to the immediate parent for grids that are not inside one of
  // the shared table shells. The shared shells are deliberately preferred:
  // their height remains stable while the grid switches between intrinsic
  // and scrolling layouts, so the decision cannot feed back into itself.
  if (!gridHost) return gridRoot.parentElement?.clientHeight ?? 0;

  const containerStyles = window.getComputedStyle(layoutContainer);
  const verticalPadding =
    (Number.parseFloat(containerStyles.paddingTop) || 0) +
    (Number.parseFloat(containerStyles.paddingBottom) || 0);
  const rowGap = Number.parseFloat(containerStyles.rowGap || containerStyles.gap) || 0;
  const occupiedHeight = visibleChildren.reduce((total, child) => {
    if (child === gridHost) return total;

    const styles = window.getComputedStyle(child);
    return (
      total +
      child.getBoundingClientRect().height +
      (Number.parseFloat(styles.marginTop) || 0) +
      (Number.parseFloat(styles.marginBottom) || 0)
    );
  }, 0);
  const gapsHeight = rowGap * Math.max(0, visibleChildren.length - 1);

  return Math.max(0, layoutContainer.clientHeight - verticalPadding - occupiedHeight - gapsHeight);
};

let measurementContext;

const getMeasurementContext = () => {
  if (measurementContext || typeof document === 'undefined') return measurementContext;

  measurementContext = document.createElement('canvas').getContext('2d');
  return measurementContext;
};

const measureText = (value, fontWeight = 400) => {
  const text = String(value ?? '');
  const context = getMeasurementContext();

  if (!context) return text.length * 7.5;

  context.font = `${fontWeight} 14px Inter, sans-serif`;
  return context.measureText(text).width;
};

const measureRenderedText = (element) => {
  const renderedText = String(element?.innerText ?? element?.textContent ?? '');

  if (!element || !renderedText.trim()) return 0;

  // Measure the browser's actual glyph geometry first. Canvas estimates can
  // differ by a few pixels from the rendered font at non-default browser zoom
  // levels, which is enough to leave an ellipsis even on a very wide screen.
  // Group text fragments that share a visual line so cells composed from
  // multiple inline nodes are measured as one value.
  if (typeof document !== 'undefined' && typeof document.createRange === 'function') {
    const textWalker = document.createTreeWalker(element, 4);
    const renderedLines = [];
    let textNode = textWalker.nextNode();

    while (textNode) {
      if (String(textNode.textContent ?? '').trim()) {
        const range = document.createRange();
        range.selectNodeContents(textNode);

        Array.from(range.getClientRects()).forEach((rect) => {
          if (rect.width <= 0) return;

          const matchingLine = renderedLines.find(
            (line) => Math.abs(line.top - rect.top) <= SIZE_CHANGE_TOLERANCE
          );

          if (matchingLine) {
            matchingLine.left = Math.min(matchingLine.left, rect.left);
            matchingLine.right = Math.max(matchingLine.right, rect.right);
          } else {
            renderedLines.push({ left: rect.left, right: rect.right, top: rect.top });
          }
        });

        range.detach?.();
      }

      textNode = textWalker.nextNode();
    }

    const exactWidth = renderedLines.reduce(
      (widestLine, line) => Math.max(widestLine, line.right - line.left),
      0
    );
    const nonTextContentWidth = Array.from(element.querySelectorAll('img, input, svg')).reduce(
      (totalWidth, contentElement) => {
        const rect = contentElement.getBoundingClientRect();
        const styles = window.getComputedStyle(contentElement);

        return (
          totalWidth +
          rect.width +
          (Number.parseFloat(styles.marginLeft) || 0) +
          (Number.parseFloat(styles.marginRight) || 0)
        );
      },
      0
    );

    if (exactWidth > 0 || nonTextContentWidth > 0) {
      return exactWidth + nonTextContentWidth;
    }
  }

  // Retain canvas as a fallback for test DOMs and older browser environments
  // that do not expose usable Range geometry.
  const context = getMeasurementContext();
  if (!context) return 0;

  const styles = window.getComputedStyle(element);
  context.font =
    styles.font && styles.font !== ''
      ? styles.font
      : `${styles.fontWeight || 400} ${styles.fontSize || '14px'} ${styles.fontFamily || 'Inter, sans-serif'}`;

  return renderedText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce((widestLine, line) => Math.max(widestLine, context.measureText(line).width), 0);
};

const humanizeField = (field = '') =>
  String(field)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const toDisplayLines = (value) => {
  if (value === null || value === undefined || value === '') return ['-'];

  if (Array.isArray(value)) {
    return [value.map((item) => toDisplayLines(item).join(' ')).join('  ') || '-'];
  }

  if (value instanceof Date) return [value.toLocaleString()];

  if (typeof value === 'boolean') return [value ? 'Yes' : 'No'];

  if (typeof value === 'object') {
    const preferredValue = value.label ?? value.name ?? value.title ?? value.value;
    if (preferredValue !== undefined) return toDisplayLines(preferredValue);

    const primitiveValues = Object.values(value).filter(
      (item) => ['string', 'number', 'boolean'].includes(typeof item) && item !== ''
    );
    return primitiveValues.length ? [primitiveValues.join(' ')] : ['-'];
  }

  return String(value).split(/\r?\n/);
};

const getColumnValue = (column, row) => {
  if (typeof column.compactValueGetter === 'function') {
    return column.compactValueGetter(row);
  }

  const field = String(column.field ?? '');
  const normalizedField = field.toLowerCase();

  if (normalizedField.includes('firstname') && row?.lastName !== undefined) {
    return `${row?.[field] ?? row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim();
  }

  if (normalizedField === 'address1' || normalizedField === 'mailingaddress') {
    return [
      `${row?.address1 ?? ''} ${row?.address2 ?? ''}`.trim(),
      `${row?.city ?? ''}${row?.city && row?.state ? ', ' : ''}${row?.state ?? ''} ${row?.zip ?? ''}`.trim(),
    ]
      .filter(Boolean)
      .join('\n');
  }

  const rawValue = row?.[field];

  if (typeof column.valueGetter === 'function') {
    try {
      return column.valueGetter({ field, row, value: rawValue });
    } catch {
      // Some legacy value getters depend on the live grid API. The raw field
      // remains a safe sizing fallback in those cases.
    }
  }

  if (typeof column.valueFormatter === 'function') {
    try {
      return column.valueFormatter({ field, row, value: rawValue });
    } catch {
      // Fall back to the raw value when a formatter requires grid-only state.
    }
  }

  return rawValue;
};

const isControlColumn = (column) => {
  const field = String(column.field ?? '')
    .trim()
    .toLowerCase();
  const header = String(column.headerName ?? '').trim();

  return (
    column.type === 'actions' ||
    field === '' ||
    field === 'actions' ||
    field === 'action' ||
    (!header && Number(column.width) > 0 && Number(column.width) <= 100)
  );
};

const appendClassName = (existingClassName, className) => {
  if (typeof existingClassName === 'function') {
    return (params) => [existingClassName(params), className].filter(Boolean).join(' ');
  }

  return [existingClassName, className].filter(Boolean).join(' ');
};

const getCompactColumnWidth = (column, rows) => {
  const explicitCompactWidth = Number(column.compactWidth);

  if (explicitCompactWidth > 0) {
    return explicitCompactWidth;
  }

  if (isControlColumn(column)) {
    const configuredWidth = Number(column.width ?? column.minWidth);
    const header =
      typeof column.headerName === 'string' || typeof column.headerName === 'number'
        ? String(column.headerName).trim()
        : '';
    const headerWidth = header ? measureText(header, 600) + CELL_GUTTER + INTER_COLUMN_SPACING : 0;

    return Math.ceil(Math.max(CONTROL_COLUMN_WIDTH, configuredWidth || 0, headerWidth));
  }

  const header = column.headerName || humanizeField(column.field);
  let widestContent = measureText(header, 600);

  rows.forEach((row) => {
    toDisplayLines(getColumnValue(column, row)).forEach((line) => {
      widestContent = Math.max(widestContent, measureText(line));
    });
  });

  const sortAllowance = column.sortable === false ? 0 : 20;
  const minimum = column.compactMinWidth ?? column.minWidth ?? DEFAULT_MIN_WIDTH;
  const measuredWidth = Math.max(
    minimum,
    widestContent + CELL_GUTTER + INTER_COLUMN_SPACING + sortAllowance
  );
  const maximum = column.compactMaxWidth ?? column.maxWidth ?? DEFAULT_MAX_WIDTH;

  return Math.ceil(Math.min(maximum, measuredWidth));
};

const compactColumns = (columns, rows) => {
  const rightActionColumnIndex =
    columns.length > 0 && isControlColumn(columns.at(-1)) ? columns.length - 1 : -1;

  return columns.map((column, columnIndex) => {
    if (column.compact === false) return column;

    const {
      compact,
      compactFill,
      compactGrow,
      compactMinWidth,
      compactMaxWidth,
      compactShrink,
      compactShrinkMinWidth,
      compactShrinkPriority,
      compactValueGetter,
      compactWidth,
      ...gridColumn
    } = column;
    const width = getCompactColumnWidth(column, rows);

    if (columnIndex === rightActionColumnIndex) {
      return {
        ...gridColumn,
        align: 'right',
        cellClassName: appendClassName(gridColumn.cellClassName, RIGHT_ACTION_CELL_CLASS),
        flex: 0,
        headerAlign: 'right',
        headerClassName: appendClassName(gridColumn.headerClassName, RIGHT_ACTION_HEADER_CLASS),
        maxWidth: width,
        minWidth: width,
        width,
      };
    }

    return {
      ...gridColumn,
      flex: 0,
      minWidth: Math.min(width, gridColumn.minWidth ?? DEFAULT_MIN_WIDTH),
      maxWidth: compactMaxWidth ?? width,
      width,
    };
  });
};

const applyRenderedContentWidths = (
  columns,
  renderedContentWidths,
  visibilityModel,
  viewportWidth
) => {
  if (viewportWidth <= 0 || Object.keys(renderedContentWidths).length === 0) return columns;

  const visibleColumnIndexes = columns.reduce((indexes, column, columnIndex) => {
    if (!column.hide && visibilityModel[column.field] !== false) indexes.push(columnIndex);
    return indexes;
  }, []);
  const requiredWidth = visibleColumnIndexes.reduce(
    (total, columnIndex) =>
      total +
      Number(columns[columnIndex].width ?? columns[columnIndex].minWidth ?? DEFAULT_MIN_WIDTH),
    0
  );
  const spareWidth = viewportWidth - requiredWidth;

  // Compact widths remain the starting point. As the viewport grows, divide
  // spare space between columns that still truncate real rendered content.
  // Columns stop growing at their content width; only the action column keeps
  // any space left after all visible values are complete.
  if (spareWidth <= SIZE_CHANGE_TOLERANCE) return columns;

  const requestedGrowth = columns.map((column, columnIndex) => {
    if (!visibleColumnIndexes.includes(columnIndex) || isControlColumn(column)) return 0;

    const currentWidth = Number(column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH);
    const renderedWidth = Number(renderedContentWidths[column.field]) || currentWidth;

    return Math.max(0, renderedWidth - currentWidth);
  });
  const totalRequestedGrowth = requestedGrowth.reduce((total, growth) => total + growth, 0);

  if (totalRequestedGrowth <= SIZE_CHANGE_TOLERANCE) return columns;

  const growthScale = Math.min(1, spareWidth / totalRequestedGrowth);

  return columns.map((column, columnIndex) => {
    const growth = requestedGrowth[columnIndex];
    if (growth <= SIZE_CHANGE_TOLERANCE) return column;

    const currentWidth = Number(column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH);
    const configuredMaximum = Number(column.maxWidth);
    const requestedWidth = Math.ceil(currentWidth + growth * growthScale);
    const width =
      configuredMaximum > 0 ? Math.min(configuredMaximum, requestedWidth) : requestedWidth;
    return { ...column, maxWidth: width, width };
  });
};

const alignActionColumnToViewport = (columns, visibilityModel, viewportWidth) => {
  if (viewportWidth <= 0) return columns;

  const visibleColumnIndexes = columns.reduce((indexes, column, columnIndex) => {
    if (!column.hide && visibilityModel[column.field] !== false) indexes.push(columnIndex);
    return indexes;
  }, []);
  const requiredWidth = visibleColumnIndexes.reduce(
    (total, columnIndex) =>
      total +
      Number(columns[columnIndex].width ?? columns[columnIndex].minWidth ?? DEFAULT_MIN_WIDTH),
    0
  );

  // Content-sized columns remain adjacent at every viewport width. If their
  // measured widths exceed the viewport, the grid scrolls horizontally.
  if (requiredWidth >= viewportWidth) return columns;

  const lastVisibleColumnIndex = visibleColumnIndexes.at(-1);
  const rightControlColumnIndex =
    lastVisibleColumnIndex !== undefined && isControlColumn(columns[lastVisibleColumnIndex])
      ? lastVisibleColumnIndex
      : undefined;
  if (rightControlColumnIndex === undefined) return columns;

  return columns.map((column, columnIndex) => {
    if (columnIndex !== rightControlColumnIndex) return column;

    const width = Number(column.width ?? column.minWidth ?? CONTROL_COLUMN_WIDTH);

    // Native grid flex keeps the action responsive when the container is
    // resized, without re-measuring a width that was inflated on the previous
    // frame. Every data column remains fixed at its content-measured width.
    return { ...column, flex: 1, maxWidth: undefined, minWidth: width, width };
  });
};

const fillDataColumnsToViewport = (columns, sourceColumns, visibilityModel, viewportWidth) => {
  if (viewportWidth <= 0) return columns;

  const visibleColumnIndexes = columns.reduce((indexes, column, columnIndex) => {
    if (!column.hide && visibilityModel[column.field] !== false) indexes.push(columnIndex);
    return indexes;
  }, []);
  const requiredWidth = visibleColumnIndexes.reduce(
    (total, columnIndex) =>
      total +
      Number(columns[columnIndex].width ?? columns[columnIndex].minWidth ?? DEFAULT_MIN_WIDTH),
    0
  );

  // Preserve content-sized columns and horizontal scrolling on constrained
  // screens. Only distribute space that is genuinely spare.
  if (requiredWidth >= viewportWidth) return columns;

  const expandableColumnIndexes = visibleColumnIndexes.filter((columnIndex) => {
    const column = columns[columnIndex];
    const sourceColumn = sourceColumns[columnIndex] ?? {};

    return (
      !isControlColumn(column) &&
      sourceColumn.compactFill !== false &&
      !(Number(sourceColumn.compactWidth) > 0)
    );
  });

  if (expandableColumnIndexes.length === 0) {
    return alignActionColumnToViewport(columns, visibilityModel, viewportWidth);
  }

  const getGrowthWeight = (columnIndex) => {
    const sourceColumn = sourceColumns[columnIndex] ?? {};
    const explicitWeight = Number(sourceColumn.compactGrow ?? sourceColumn.flex);

    return explicitWeight > 0 ? explicitWeight : 1;
  };
  const totalGrowthWeight = expandableColumnIndexes.reduce(
    (total, columnIndex) => total + getGrowthWeight(columnIndex),
    0
  );
  const spareWidth = viewportWidth - requiredWidth;
  const nextWidths = columns.map((column) =>
    Number(column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH)
  );

  expandableColumnIndexes.forEach((columnIndex) => {
    nextWidths[columnIndex] += spareWidth * (getGrowthWeight(columnIndex) / totalGrowthWeight);
  });

  const roundedWidths = nextWidths.map((width) => Math.round(width));
  const roundedVisibleWidth = visibleColumnIndexes.reduce(
    (total, columnIndex) => total + roundedWidths[columnIndex],
    0
  );
  roundedWidths[expandableColumnIndexes.at(-1)] += Math.round(viewportWidth) - roundedVisibleWidth;

  return columns.map((column, columnIndex) => {
    if (!visibleColumnIndexes.includes(columnIndex)) return column;

    const width = roundedWidths[columnIndex];
    return { ...column, flex: 0, maxWidth: width, minWidth: width, width };
  });
};

const shrinkDataColumnsToViewport = (columns, sourceColumns, visibilityModel, viewportWidth) => {
  if (viewportWidth <= 0) return columns;

  const visibleColumnIndexes = columns.reduce((indexes, column, columnIndex) => {
    if (!column.hide && visibilityModel[column.field] !== false) indexes.push(columnIndex);
    return indexes;
  }, []);
  const currentWidths = columns.map((column) =>
    Number(column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH)
  );
  const requiredWidth = visibleColumnIndexes.reduce(
    (total, columnIndex) => total + currentWidths[columnIndex],
    0
  );
  const overflowWidth = requiredWidth - viewportWidth;

  if (overflowWidth <= SIZE_CHANGE_TOLERANCE) return columns;

  const shrinkableColumnIndexes = visibleColumnIndexes.filter((columnIndex) => {
    const column = columns[columnIndex];
    const sourceColumn = sourceColumns[columnIndex] ?? {};

    return !isControlColumn(column) && sourceColumn.compactShrink !== false;
  });
  const minimumWidths = currentWidths.map((currentWidth, columnIndex) => {
    const configuredMinimum = Number(sourceColumns[columnIndex]?.compactShrinkMinWidth);
    return Math.min(currentWidth, configuredMinimum > 0 ? configuredMinimum : DEFAULT_MIN_WIDTH);
  });
  const totalShrinkCapacity = shrinkableColumnIndexes.reduce(
    (total, columnIndex) => total + currentWidths[columnIndex] - minimumWidths[columnIndex],
    0
  );

  if (totalShrinkCapacity + SIZE_CHANGE_TOLERANCE < overflowWidth) return columns;

  const nextWidths = [...currentWidths];
  const shrinkPriorities = [
    ...new Set(
      shrinkableColumnIndexes.map((columnIndex) => {
        const priority = Number(sourceColumns[columnIndex]?.compactShrinkPriority);
        return Number.isFinite(priority) ? priority : 0;
      })
    ),
  ].sort((leftPriority, rightPriority) => leftPriority - rightPriority);
  let remainingOverflowWidth = overflowWidth;

  shrinkPriorities.forEach((priority) => {
    if (remainingOverflowWidth <= SIZE_CHANGE_TOLERANCE) return;

    const priorityColumnIndexes = shrinkableColumnIndexes.filter((columnIndex) => {
      const configuredPriority = Number(sourceColumns[columnIndex]?.compactShrinkPriority);
      const columnPriority = Number.isFinite(configuredPriority) ? configuredPriority : 0;
      return columnPriority === priority;
    });
    const priorityShrinkCapacity = priorityColumnIndexes.reduce(
      (total, columnIndex) => total + currentWidths[columnIndex] - minimumWidths[columnIndex],
      0
    );
    if (priorityShrinkCapacity <= SIZE_CHANGE_TOLERANCE) return;

    const shrinkWidth = Math.min(remainingOverflowWidth, priorityShrinkCapacity);

    priorityColumnIndexes.forEach((columnIndex) => {
      const shrinkCapacity = currentWidths[columnIndex] - minimumWidths[columnIndex];
      nextWidths[columnIndex] -= shrinkWidth * (shrinkCapacity / priorityShrinkCapacity);
    });
    remainingOverflowWidth -= shrinkWidth;
  });

  const roundedWidths = nextWidths.map((width) => Math.round(width));
  const roundedVisibleWidth = visibleColumnIndexes.reduce(
    (total, columnIndex) => total + roundedWidths[columnIndex],
    0
  );
  roundedWidths[shrinkableColumnIndexes.at(-1)] += Math.round(viewportWidth) - roundedVisibleWidth;

  return columns.map((column, columnIndex) => {
    if (!visibleColumnIndexes.includes(columnIndex)) return column;

    const width = roundedWidths[columnIndex];
    return { ...column, flex: 0, maxWidth: width, minWidth: width, width };
  });
};

const OlcDataGrid = forwardRef(function OlcDataGrid(
  {
    autoHeight: requestedAutoHeight,
    columns = [],
    rows = [],
    compactColumns: useCompactColumns = true,
    measureContentWidth = true,
    fillAvailableWidth = true,
    preserveColumnLayout = false,
    shrinkColumnsToFit = false,
    className = '',
    ...props
  },
  ref
) {
  const gridRootRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(0);
  const [renderedContentWidths, setRenderedContentWidths] = useState({});
  const [viewportWidth, setViewportWidth] = useState(0);

  const estimatedContentHeight =
    Number(props.columnHeaderHeight ?? DEFAULT_COLUMN_HEADER_HEIGHT) +
    rows.length * Number(props.rowHeight ?? DEFAULT_ROW_HEIGHT);
  const contentFitsAvailableHeight =
    availableHeight > 0
      ? estimatedContentHeight <= availableHeight
      : rows.length < DEFAULT_PAGE_SIZE;
  const autoHeight =
    requestedAutoHeight ?? (!props.loading && (rows.length === 0 || contentFitsAvailableHeight));

  const baseColumns = useMemo(
    () => (useCompactColumns ? compactColumns(columns, rows) : columns),
    [columns, rows, useCompactColumns]
  );

  const visibilityModel = useMemo(() => {
    const initialVisibilityModel = props.initialState?.columns?.columnVisibilityModel ?? {};
    return props.columnVisibilityModel ?? initialVisibilityModel;
  }, [props.columnVisibilityModel, props.initialState]);

  const contentSizedColumns = useMemo(
    () =>
      measureContentWidth
        ? applyRenderedContentWidths(
            baseColumns,
            renderedContentWidths,
            visibilityModel,
            viewportWidth
          )
        : baseColumns,
    [baseColumns, measureContentWidth, renderedContentWidths, viewportWidth, visibilityModel]
  );

  const layoutColumns = useMemo(() => {
    if (preserveColumnLayout) return contentSizedColumns;

    let nextColumns;

    if (fillAvailableWidth) {
      nextColumns = fillDataColumnsToViewport(
        contentSizedColumns,
        columns,
        visibilityModel,
        viewportWidth
      );
    } else {
      nextColumns = alignActionColumnToViewport(
        contentSizedColumns,
        visibilityModel,
        viewportWidth
      );
    }

    return shrinkColumnsToFit
      ? shrinkDataColumnsToViewport(nextColumns, columns, visibilityModel, viewportWidth)
      : nextColumns;
  }, [
    columns,
    contentSizedColumns,
    fillAvailableWidth,
    preserveColumnLayout,
    shrinkColumnsToFit,
    viewportWidth,
    visibilityModel,
  ]);
  const minimumRequiredWidth = useMemo(() => {
    return layoutColumns.reduce((total, column) => {
      if (column.hide || visibilityModel[column.field] === false) return total;

      return total + Number(column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH);
    }, 0);
  }, [layoutColumns, visibilityModel]);
  const fitsViewport = viewportWidth > 0 && minimumRequiredWidth <= viewportWidth;
  const slots = useMemo(
    () => ({
      ...props.slots,
      // Loading is intentionally owned by the shared grid. Keep this after
      // consumer slots so a page cannot reintroduce different loader sizing.
      loadingOverlay: Loader,
    }),
    [props.slots]
  );

  const setGridRef = useCallback(
    (node) => {
      gridRootRef.current = node;

      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  // The first render commonly contains no rows while a server request is in
  // flight. Re-measure when the result count changes so a later short page
  // cannot keep the zero-height fallback and push pagination out of the card.
  useLayoutEffect(() => {
    let resizeObserver;
    let removeWindowListener;
    let scheduledFrame;
    let observedScroller;
    let observedLayoutContainer;

    const updateGridViewport = () => {
      // MUI can commit the root before its virtual scroller. Query on every
      // attempt instead of permanently giving up when a cached route mounts
      // with rows already present.
      const gridRoot = gridRootRef.current;
      const scroller = gridRoot?.querySelector('.MuiDataGrid-virtualScroller');

      if (!gridRoot || !scroller) return;

      const layoutContainer = getLayoutContainer(gridRoot);

      if (resizeObserver && observedScroller !== scroller) {
        if (observedScroller) resizeObserver.unobserve(observedScroller);
        resizeObserver.observe(scroller);
        observedScroller = scroller;
      }

      if (resizeObserver && observedLayoutContainer !== layoutContainer) {
        if (observedLayoutContainer) resizeObserver.unobserve(observedLayoutContainer);
        if (layoutContainer) resizeObserver.observe(layoutContainer);
        observedLayoutContainer = layoutContainer;
      }

      const nextAvailableHeight = getAvailableGridHeight(gridRoot, layoutContainer);
      const nextViewportWidth = scroller.clientWidth;

      setAvailableHeight((currentHeight) =>
        Math.abs(currentHeight - nextAvailableHeight) > SIZE_CHANGE_TOLERANCE
          ? nextAvailableHeight
          : currentHeight
      );
      setViewportWidth((currentWidth) =>
        Math.abs(currentWidth - nextViewportWidth) > SIZE_CHANGE_TOLERANCE
          ? nextViewportWidth
          : currentWidth
      );
    };
    const scheduleGridViewportUpdate = () => {
      if (scheduledFrame) return;

      scheduledFrame = window.requestAnimationFrame(() => {
        scheduledFrame = undefined;
        updateGridViewport();
      });
    };

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleGridViewportUpdate);
      removeWindowListener = () => window.removeEventListener('resize', scheduleGridViewportUpdate);
    } else {
      resizeObserver = new ResizeObserver(scheduleGridViewportUpdate);
    }

    // A layout effect runs before paint, so the first visible table already
    // has its initial column widths. The unconditional next-frame attempt is
    // essential on client-side navigation: MUI may not have committed the
    // virtual scroller during this first attempt when Redux rows are cached.
    updateGridViewport();
    scheduleGridViewportUpdate();

    return () => {
      if (scheduledFrame) window.cancelAnimationFrame(scheduledFrame);
      resizeObserver?.disconnect();
      removeWindowListener?.();
    };
  }, [rows.length]);

  useLayoutEffect(() => {
    const gridRoot = gridRootRef.current;
    if (!measureContentWidth || !gridRoot || viewportWidth <= 0) return undefined;

    let scheduledFrame = window.requestAnimationFrame(() => {
      const nextRenderedWidths = {};

      gridRoot.querySelectorAll('.MuiDataGrid-cell[data-field]').forEach((cell) => {
        const field = cell.getAttribute('data-field');
        const column = layoutColumns.find((item) => String(item.field) === field);

        if (!field || !column || isControlColumn(column)) return;

        const descendants = [...cell.querySelectorAll('*')];

        // Some legacy cell styles hard-code a width on the Typography itself.
        // Normalize only text nodes that are actually overflowing so the text
        // can first use its existing cell before the column requests more room.
        descendants.forEach((element) => {
          if (
            element.clientWidth > 0 &&
            element.scrollWidth - element.clientWidth > SIZE_CHANGE_TOLERANCE &&
            (element.matches('.MuiTypography-root, p') ||
              window.getComputedStyle(element).textOverflow === 'ellipsis')
          ) {
            element.classList.add(RESPONSIVE_TEXT_CLASS);
          }
        });

        const currentWidth = cell.getBoundingClientRect().width;
        const baseColumn = baseColumns.find((item) => String(item.field) === field);
        const baseWidth = Number(baseColumn?.width ?? baseColumn?.minWidth ?? DEFAULT_MIN_WIDTH);
        const overflowWidth = [cell, ...descendants].reduce((largestOverflow, element) => {
          if (element.clientWidth <= 0) return largestOverflow;

          return Math.max(largestOverflow, element.scrollWidth - element.clientWidth);
        }, 0);
        const renderedTextWidth = measureRenderedText(cell);
        const requestedWidth = Math.max(
          overflowWidth > SIZE_CHANGE_TOLERANCE ? currentWidth + overflowWidth : baseWidth,
          renderedTextWidth + CELL_GUTTER + INTER_COLUMN_SPACING
        );

        nextRenderedWidths[field] = Math.max(nextRenderedWidths[field] ?? 0, requestedWidth);
      });

      setRenderedContentWidths((currentWidths) => {
        const currentFields = Object.keys(currentWidths);
        const nextFields = Object.keys(nextRenderedWidths);
        const unchanged =
          currentFields.length === nextFields.length &&
          nextFields.every(
            (field) =>
              Math.abs((currentWidths[field] ?? 0) - nextRenderedWidths[field]) <=
              SIZE_CHANGE_TOLERANCE
          );

        return unchanged ? currentWidths : nextRenderedWidths;
      });
    });

    return () => {
      if (scheduledFrame) window.cancelAnimationFrame(scheduledFrame);
    };
  }, [baseColumns, layoutColumns, measureContentWidth, rows, viewportWidth]);

  return (
    <MuiDataGrid
      ref={setGridRef}
      {...props}
      autoHeight={autoHeight}
      className={[
        'olcDataGrid',
        autoHeight ? 'olcDataGridAutoHeight' : '',
        props.loading ? 'olcDataGridLoading' : '',
        className,
        useCompactColumns ? 'olcCompactDataGrid' : '',
        useCompactColumns && fitsViewport ? 'olcDataGridFitsViewport' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      columnBuffer={props.columnBuffer ?? layoutColumns.length}
      columns={layoutColumns}
      rows={rows}
      slots={slots}
    />
  );
});

export default OlcDataGrid;
